<template>
    <div class="container">
        <loading-component v-if="isLoading" />
        <no-result-component v-else-if="contestants == null || contestants.length <= 0" />
        <div v-else>
            <page-title-component title="Result" :subtitle="competition.name" />
            <section class="section">
                <div class="container">
                    <div v-for="competitionClass in competition.classes"
                         :key="competitionClass.name"
                         class="container">
                        <h2 class="subtitle">
                            Class:
                            <span :class="competitionClass.cssClass">{{ competitionClass.name }}</span>
                        </h2>

                        <!-- Desktop view -->
                        <b-table :data="getClassContestants(competitionClass.id)" ref="table" :striped="true" class="d-none d-md-table">
                            <template slot-scope="props">
                                <b-table-column field="placing" label="Placing">
                                    <span v-if="props.row.placing == 99999">-</span>
                                    <span v-else>{{ props.row.placing }}</span>
                                </b-table-column>
                                <b-table-column v-if="competitionClass.competitionClassType === CompetitionClassType.bayesianVote" field="bayesianScore" label="Weighted Score">
                                    <span v-if="props.row.placing == 99999">-</span>
                                    <span v-else>{{ Number(props.row.bayesianScore).toFixed(3) }}</span>
                                </b-table-column>
                                <b-table-column v-else field="score" label="Score">
                                    <span v-if="props.row.placing == 99999">-</span>
                                    <span v-else>{{ props.row.score }}</span>
                                </b-table-column>
                                <b-table-column field="tiebreaker" :label="competitionClass.competitionClassType === CompetitionClassType.bayesianVote ? 'Number of votes (Tiebreaker 1)' : 'Tiebreaker'">
                                    <div v-if="props.row.placing == 99999">
                                        <span>-</span>
                                    </div>
                                    <div v-else>
                                        <span v-if="competitionClass.competitionClassType === CompetitionClassType.bayesianVote" class="no-break">
                                            <span>{{ props.row.bayesianTieBreaker_1 }}</span> <!-- Tiebreaker 1 in Bayesian is the number of votes -->
                                        </span>
                                        <span v-else-if="competitionClass.competitionClassType === CompetitionClassType.singleVote" class="no-break">
                                            <span class="step_gold">{{ props.row.score }}</span>
                                            <span class="step_silver">0</span>
                                            <span class="step_bronze">0</span>
                                        </span>
                                        <span v-else class="no-break">
                                            <span class="step_gold">{{ props.row.tiebreaker_1 }}</span>
                                            <span class="step_silver">{{ props.row.tiebreaker_2 }}</span>
                                            <span class="step_bronze">{{ props.row.tiebreaker_3 }}</span>
                                        </span>
                                    </div>
                                </b-table-column>
                                <b-table-column field="tiebreaker2" v-if="competitionClass.competitionClassType === CompetitionClassType.bayesianVote" label="Median score (Tiebreaker 2)">
                                    <template #label>
                                        Median score
                                        <br> (Tiebreaker 2)
                                    </template>
                                    <div v-if="props.row.placing == 99999">
                                        <span>-</span>
                                    </div>
                                    <div v-else>
                                        <span class="no-break">
                                            <span>{{ Number(props.row.bayesianTieBreaker_2).toFixed(3) }}</span> <!-- Tiebreaker 2 in Bayesian is the Median -->
                                        </span>
                                    </div>
                                </b-table-column>
                                <b-table-column field="tiebreaker3" v-if="competitionClass.competitionClassType === CompetitionClassType.bayesianVote" label="Standard deviation score (Tiebreaker 3)">
                                    <div v-if="props.row.placing == 99999">
                                        <span>-</span>
                                    </div>
                                    <div v-else>
                                        <span class="no-break">
                                            <span>{{ Number(props.row.bayesianTieBreaker_3).toFixed(3) }}</span> <!-- Tiebreaker 3 in Bayesian is the Standard deviation  -->
                                        </span>
                                    </div>
                                </b-table-column>
                                <b-table-column field="brewer" label="Brewer">{{ props.row.brewer }}</b-table-column>
                                <b-table-column field="title" label="Name">{{ props.row.title }}</b-table-column>
                                <b-table-column field="style" label="Style">{{ props.row.style }}</b-table-column>
                            </template>
                        </b-table>
                        
                        <!-- Mobile view with column navigation -->
                        <div class="mobile-table-wrapper d-md-none">
                            <div class="mobile-column-nav" v-if="needsNavigation(competitionClass)">
                                <b-button 
                                    @click="previousColumnSet(competitionClass.id)" 
                                    :disabled="currentColumnIndex[competitionClass.id] === 0"
                                    size="is-small"
                                    type="is-dark"
                                    icon-left="chevron-left">
                                </b-button>
                                <span class="column-indicator">
                                    {{ getColumnIndicatorText(competitionClass) }}
                                </span>
                                <b-button 
                                    @click="nextColumnSet(competitionClass.id)" 
                                    :disabled="isLastColumnSet(competitionClass)"
                                    size="is-small"
                                    type="is-dark"
                                    icon-right="chevron-right">
                                </b-button>
                            </div>
                            <div class="mobile-tiebreaker-info" v-if="competitionClass.competitionClassType === CompetitionClassType.bayesianVote">
                                <small>
                                    <b-icon icon="information-outline" size="is-small"></b-icon>
                                    Tiebreakers: 1. Nr of votes → 2. Median → 3. Standard Deviation
                                </small>
                            </div>
                            <table class="table is-striped mobile-results-table">
                                <thead>
                                    <tr>
                                        <th class="sticky-column-place">#</th>
                                        <th class="sticky-column-brew">Brew / Brewer</th>
                                        <th v-for="col in getVisibleColumns(competitionClass)" :key="col.key">
                                            <span v-if="col.key === 'score' && competitionClass.competitionClassType === CompetitionClassType.bayesianVote">
                                                Weighted<br>Score
                                            </span>
                                            <span v-else-if="col.key === 'tiebreaker2'">
                                                Median<br>Score
                                            </span>
                                            <span v-else-if="col.key === 'tiebreaker3'">
                                                Std Dev<br>Score
                                            </span>
                                            <span v-else>{{ col.label }}</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in getClassContestants(competitionClass.id)" :key="row.id">
                                        <td class="sticky-column-place">
                                            <span v-if="row.placing == 99999">-</span>
                                            <span v-else>{{ row.placing }}</span>
                                        </td>
                                        <td class="sticky-column-brew">
                                            <div class="brew-brewer-mobile">
                                                <div class="brew-row">{{ row.title }}</div>
                                                <div class="brewer-row">{{ row.brewer }}</div>
                                            </div>
                                        </td>
                                        <td v-for="col in getVisibleColumns(competitionClass)" :key="col.key">
                                            <div v-if="col.key === 'score'">
                                                <span v-if="row.placing == 99999">-</span>
                                                <span v-else-if="competitionClass.competitionClassType === CompetitionClassType.bayesianVote">
                                                    {{ Number(row.bayesianScore).toFixed(3) }}
                                                </span>
                                                <span v-else>{{ row.score }}</span>
                                            </div>
                                            <div v-else-if="col.key === 'tiebreaker1'">
                                                <span v-if="row.placing == 99999">-</span>
                                                <span v-else-if="competitionClass.competitionClassType === CompetitionClassType.bayesianVote">
                                                    {{ row.bayesianTieBreaker_1 }}
                                                </span>
                                                <span v-else-if="competitionClass.competitionClassType === CompetitionClassType.singleVote" class="no-break">
                                                    <span class="step_gold">{{ row.score }}</span>
                                                    <span class="step_silver">0</span>
                                                    <span class="step_bronze">0</span>
                                                </span>
                                                <span v-else class="no-break">
                                                    <span class="step_gold">{{ row.tiebreaker_1 }}</span>
                                                    <span class="step_silver">{{ row.tiebreaker_2 }}</span>
                                                    <span class="step_bronze">{{ row.tiebreaker_3 }}</span>
                                                </span>
                                            </div>
                                            <div v-else-if="col.key === 'tiebreaker2'">
                                                <span v-if="row.placing == 99999">-</span>
                                                <span v-else>{{ Number(row.bayesianTieBreaker_2).toFixed(3) }}</span>
                                            </div>
                                            <div v-else-if="col.key === 'tiebreaker3'">
                                                <span v-if="row.placing == 99999">-</span>
                                                <span v-else>{{ Number(row.bayesianTieBreaker_3).toFixed(3) }}</span>
                                            </div>
                                            <div v-else-if="col.key === 'style'" class="style-column">
                                                <span :title="row.style">{{ row.style }}</span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <hr />
                    </div>
                    <div v-if="competition.brewersChoiceVotingEnabled" class="container">
                        <h2 class="subtitle">Brewer's choice</h2>
                        <b-table v-if="brewersChoiceResults.length > 0" :data="brewersChoiceResults" :striped="true">
                            <template slot-scope="props">
                                <b-table-column field="voteCount" label="Votes">{{ props.row.voteCount }}</b-table-column>
                                <b-table-column field="brewer" label="Brewer">{{ props.row.brewer }}</b-table-column>
                                <b-table-column field="brewName" label="Brew">{{ props.row.brewName }}</b-table-column>
                                <b-table-column field="displayNumber" label="Number">{{ props.row.displayNumber }}</b-table-column>
                            </template>
                        </b-table>
                        <p v-else>No brewer's choice votes were cast.</p>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>



<script>
    import apiService from "../services/apiService";
    import LoadingComponent from "./Loading.vue";
    import NoResultComponent from "./NoResult.vue";
    import PageTitleComponent from "./PageTitle.vue";
    import CompetitionClassType from "../enums/competitionClassType.js";
    export default {
        name: "competition-results",
        components: {
            LoadingComponent,
            NoResultComponent,
            PageTitleComponent
        },
        props: {
            apiKey: String,
            competitionId: String
        },

        data() {
            return {
                isLoading: false,
                onlyCompetitionEntries: true,
                contestants: [],
                competition: null,
                brewersChoiceResults: [],
                CompetitionClassType: CompetitionClassType,
                currentColumnIndex: {},
                columnsPerPage: 3 // Show 3 additional columns at a time on mobile
            };
        },
        async created() {
            this.isLoading = true;
            try {
                await this.fetch();
            } catch (err) {
                console.log(err);
            } finally {
                this.isLoading = false;
            }
        },
        methods: {
            getAvailableColumns(competitionClass) {
                const columns = [];
                
                // Score column
                columns.push({
                    key: 'score',
                    label: competitionClass.competitionClassType === this.CompetitionClassType.bayesianVote ? 'Weighted Score' : 'Score'
                });
                
                // Tiebreaker columns based on competition type
                if (competitionClass.competitionClassType === this.CompetitionClassType.bayesianVote) {
                    columns.push({
                        key: 'tiebreaker1',
                        label: 'Votes'
                    });
                    columns.push({
                        key: 'tiebreaker2',
                        label: 'Median Score'
                    });
                    columns.push({
                        key: 'tiebreaker3',
                        label: 'Std Dev Score'
                    });
                } else {
                    columns.push({
                        key: 'tiebreaker1',
                        label: 'Tiebreaker'
                    });
                }
                
                // Style column only (brewer is now combined with name)
                columns.push({
                    key: 'style',
                    label: 'Style'
                });
                
                return columns;
            },
            
            getVisibleColumns(competitionClass) {
                const allColumns = this.getAvailableColumns(competitionClass);
                const currentIndex = this.currentColumnIndex[competitionClass.id] || 0;
                return allColumns.slice(currentIndex, currentIndex + this.columnsPerPage);
            },
            
            nextColumnSet(classId) {
                const competitionClass = this.competition.classes.find(c => c.id === classId);
                const allColumns = this.getAvailableColumns(competitionClass);
                const currentIndex = this.currentColumnIndex[classId] || 0;
                
                if (currentIndex + this.columnsPerPage < allColumns.length) {
                    // Move forward, but ensure we don't go past the end
                    const remainingColumns = allColumns.length - currentIndex - this.columnsPerPage;
                    const step = Math.min(this.columnsPerPage, remainingColumns);
                    this.$set(this.currentColumnIndex, classId, currentIndex + step);
                }
            },
            
            previousColumnSet(classId) {
                const currentIndex = this.currentColumnIndex[classId] || 0;
                if (currentIndex > 0) {
                    // Move back by columnsPerPage
                    const step = Math.min(this.columnsPerPage, currentIndex);
                    this.$set(this.currentColumnIndex, classId, currentIndex - step);
                }
            },
            
            isLastColumnSet(competitionClass) {
                const allColumns = this.getAvailableColumns(competitionClass);
                const currentIndex = this.currentColumnIndex[competitionClass.id] || 0;
                return currentIndex + this.columnsPerPage >= allColumns.length;
            },
            
            needsNavigation(competitionClass) {
                const allColumns = this.getAvailableColumns(competitionClass);
                return allColumns.length > this.columnsPerPage;
            },
            
            getColumnIndicatorText(competitionClass) {
                const allColumns = this.getAvailableColumns(competitionClass);
                const currentIndex = this.currentColumnIndex[competitionClass.id] || 0;
                const visibleColumns = allColumns.slice(currentIndex, currentIndex + this.columnsPerPage);
                
                if (visibleColumns.length === 0) return '';
                if (visibleColumns.length === 1) return visibleColumns[0].label;
                
                // Show both column names
                return visibleColumns.map(col => col.label).join(' / ');
            },
            getClassContestants(classId) {
                return this.contestants.filter(function (i) {
                    return i.competitionClassId == classId;
                });
            },

            getClassLabel(classId) {
                var c = this.competition.classes.find(function (element) {
                    return element.id == classId;
                });
                if (c) {
                    return c.name;
                } else return "";
            },
            getClassClass(classId) {
                var c = this.competition.classes.find(function (element) {
                    return element.id == classId;
                });
                if (c) {
                    return c.cssClass;
                } else return "";
            },
            async fetch() {
                var self = this;

                const competitionData = await apiService.getCompetition(
                    this.competitionId
                );

                this.competition = competitionData.data;
                
                // Initialize currentColumnIndex for each competition class
                if (this.competition && this.competition.classes) {
                    this.competition.classes.forEach(competitionClass => {
                        this.$set(this.currentColumnIndex, competitionClass.id, 0);
                    });
                }

                if (this.apiKey) {
                    var managerToken = {
                        competitionId: this.competitionId,
                        apiKey: this.apiKey
                    };
                    apiService
                        .getPreResult(managerToken)
                        .then(response => {
                            this.$toast.open({
                                message: "Pre result succeeded!",
                                type: "is-success",
                                position: "is-bottom"
                            });
                            self.contestants = response.data;
                        })
                        .catch(err => {
                            this.$toast.open({
                                message: err.response.data,
                                type: "is-danger",
                                position: "is-bottom"
                            });
                        });
                } else {
                    if (!this.competition.isResultPublished) {
                        return;
                    }
                    const resultData = await apiService.getBrewResultsForCompetition(
                        this.competitionId
                    );
                    this.contestants = resultData.data;

                }

                if (this.competition && this.competition.brewersChoiceVotingEnabled) {
                    const payload = { competitionId: this.competitionId };
                    if (this.apiKey) {
                        payload.apiKey = this.apiKey;
                    }
                    try {
                        const brewersChoiceData = await apiService.getBrewersChoiceResult(payload);
                        this.brewersChoiceResults = brewersChoiceData.data;
                    } catch (err) {
                        console.log(err);
                    }
                }
            }
        }
    };
</script>
<style scoped>
    span.no-break {
        white-space: nowrap;
    }
    
    /* Mobile table styles */
    @media (max-width: 767px) {
        /* Reduce section padding on mobile */
        .section {
            padding: 1rem 1.5rem !important;
        }
        
        .container .container {
            margin-bottom: 1rem;
        }
        
        /* Reduce subtitle margin on mobile */
        h2.subtitle {
            margin-bottom: 0.75rem !important;
        }
        
        .mobile-table-wrapper {
            position: relative;
            margin: 0 -10px; /* Extend to near full width on mobile */
        }
        
        /* Column navigation controls */
        .mobile-column-nav {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            padding: 8px 10px;
            gap: 8px;
            background: #f8f8f8;
            border-bottom: 1px solid #ddd;
        }
        
        /* Style adjustments for Buefy buttons in mobile nav */
        .mobile-column-nav .button.is-small {
            padding: 0;
            height: 26px;
            width: 26px;
            min-width: 26px;
            font-size: 0.75rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .mobile-column-nav .button.is-small .icon {
            height: 1rem;
            width: 1rem;
            font-size: 14px;
            margin: 0 !important;
        }
        
        .mobile-column-nav .button.is-dark {
            background-color: #363636;
            border-color: #363636;
        }
        
        .mobile-column-nav .button.is-dark:hover:not(:disabled) {
            background-color: #4a4a4a;
            border-color: #4a4a4a;
        }
        
        .mobile-column-nav .button.is-dark:disabled {
            opacity: 0.3;
            background-color: #363636;
        }
        
        .column-indicator {
            font-size: 12px;
            color: #666;
            font-weight: 500;
            text-align: center;
            padding: 0 8px;
            min-width: 80px;
        }
        
        .mobile-tiebreaker-info {
            padding: 8px 10px;
            background: #f0f8ff;
            border-bottom: 1px solid #ddd;
            font-size: 12px;
            color: #555;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .mobile-tiebreaker-info small {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;
        }
        
        .mobile-tiebreaker-info .icon {
            color: #3273dc;
        }
        
        .mobile-results-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            font-size: 12px; /* Moderately reduced font size */
        }
        
        .mobile-results-table thead tr {
            background: #f5f5f5;
        }
        
        .mobile-results-table th,
        .mobile-results-table td {
            padding: 6px 6px; /* Consistent padding */
            border-bottom: 1px solid #dbdbdb;
            border-right: 1px solid #e8e8e8;
            white-space: nowrap;
            font-size: 12px; /* Ensure consistent font */
            text-align: left;
        }
        
        .mobile-results-table th {
            line-height: 1.2;
            vertical-align: middle;
        }
        
        .mobile-results-table th:last-child,
        .mobile-results-table td:last-child {
            border-right: none;
        }
        
        .mobile-results-table tbody tr:nth-child(even) {
            background-color: #fafafa;
        }
        
        /* Sticky columns */
        .sticky-column-place {
            position: sticky;
            left: 0;
            background: inherit;
            z-index: 10;
            min-width: 25px; /* Minimal width for place column */
            max-width: 25px;
            padding: 6px 4px !important;
            text-align: center !important;
            border-right: 2px solid #d0d0d0 !important;
        }
        
        .sticky-column-brew {
            position: sticky;
            left: 25px; /* Adjust based on place column width */
            background: inherit;
            z-index: 9;
            min-width: 120px;
            max-width: 120px;
            border-right: 2px solid #d0d0d0 !important;
            padding: 6px 6px !important;
            overflow: hidden;
        }
        
        /* Brew/brewer mobile styles */
        .brew-brewer-mobile {
            width: 100%;
            overflow: hidden;
        }
        
        .brew-brewer-mobile .brew-row {
            font-size: 12px;
            font-weight: bold;
            color: #333;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        
        .brew-brewer-mobile .brewer-row {
            font-size: 11px;
            color: #666;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        
        /* Ensure sticky columns have proper background */
        .mobile-results-table thead .sticky-column-place,
        .mobile-results-table thead .sticky-column-brew {
            background: #f5f5f5;
        }
        
        .mobile-results-table tbody tr:nth-child(even) .sticky-column-place,
        .mobile-results-table tbody tr:nth-child(even) .sticky-column-brew {
            background-color: #fafafa;
        }
        
        .mobile-results-table tbody tr:nth-child(odd) .sticky-column-place,
        .mobile-results-table tbody tr:nth-child(odd) .sticky-column-brew {
            background-color: white;
        }
        
        /* Additional column styles */
        .mobile-results-table td:not(.sticky-column-place):not(.sticky-column-brew) {
            min-width: 50px;
        }
        
        /* Style column truncation */
        .style-column {
            max-width: 80px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        
        .style-column span {
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
        
        /* Step indicators for tiebreakers */
        .mobile-results-table .step_gold,
        .mobile-results-table .step_silver,
        .mobile-results-table .step_bronze {
            font-size: 11px;
            padding: 0 2px;
        }
        
        .mobile-results-table .no-break {
            white-space: nowrap;
        }
    }
    
    /* Bootstrap utility classes for responsive display */
    @media (max-width: 767px) {
        .d-none.d-md-table {
            display: none !important;
        }
        .d-md-none {
            display: block !important;
        }
    }
    
    @media (min-width: 768px) {
        .d-none.d-md-table {
            display: table !important;
        }
        .d-md-none {
            display: none !important;
        }
    }
</style>